import {
  DEFAULT_TIMEZONE,
  LEGACY_TIMEZONE_ALIASES,
  TIMEZONE_IDENTIFIER_ALIASES,
  TIMEZONE_OPTIONS,
  getTimezoneDisplayName,
  getTimezoneOptionLabel,
} from '@/js/core/utils/timezone-options';

export { DEFAULT_TIMEZONE, TIMEZONE_OPTIONS, getTimezoneOptionLabel };

const TIMEZONE_LABEL_LOCALES = ['en-US', 'en-GB', 'en-AU', 'en-NZ', 'en-CA', 'en-IN'];
const GMT_OFFSET_PATTERN = /^(?:GMT|UTC)[+-]\d/i;

function partsToObject(parts) {
  return Object.fromEntries(
    parts.filter(part => part.type !== 'literal').map(part => [part.type, part.value])
  );
}

function toDate(value) {
  return value instanceof Date ? value : new Date(value);
}

function isValidDate(date) {
  return date instanceof Date && !Number.isNaN(date.getTime());
}

function formatOffsetMinutes(minutes) {
  const sign = minutes >= 0 ? '+' : '-';
  const absoluteMinutes = Math.abs(minutes);
  const hours = Math.floor(absoluteMinutes / 60)
    .toString()
    .padStart(2, '0');
  const remainingMinutes = absoluteMinutes % 60;

  return remainingMinutes === 0
    ? `GMT${sign}${hours}`
    : `GMT${sign}${hours}:${remainingMinutes.toString().padStart(2, '0')}`;
}

function timezoneNamePart(date, timezone, locale, style = 'short') {
  try {
    return new Intl.DateTimeFormat(locale, {
      timeZone: timezone,
      timeZoneName: style,
    })
      .formatToParts(date)
      .find(part => part.type === 'timeZoneName')?.value;
  } catch {
    return '';
  }
}

export function detectTimezone(fallback = DEFAULT_TIMEZONE) {
  try {
    return normalizeTimezone(Intl.DateTimeFormat().resolvedOptions().timeZone, fallback);
  } catch {
    return fallback;
  }
}

export function normalizeTimezone(timezone, fallback = DEFAULT_TIMEZONE) {
  const candidate =
    LEGACY_TIMEZONE_ALIASES[timezone] || TIMEZONE_IDENTIFIER_ALIASES[timezone] || timezone;

  if (!candidate || typeof candidate !== 'string') {
    return fallback;
  }

  try {
    new Intl.DateTimeFormat('en-US', { timeZone: candidate }).format();
    return candidate;
  } catch {
    return fallback;
  }
}

export function getZonedParts(value, timezone, extraOptions = {}) {
  return partsToObject(
    new Intl.DateTimeFormat('en-US', {
      timeZone: normalizeTimezone(timezone),
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hourCycle: 'h23',
      ...extraOptions,
    }).formatToParts(toDate(value))
  );
}

export function getTimezoneOffsetMs(value, timezone) {
  const date = toDate(value);
  const parts = getZonedParts(date, timezone);
  const zonedUtc = Date.UTC(
    Number(parts.year),
    Number(parts.month) - 1,
    Number(parts.day),
    Number(parts.hour),
    Number(parts.minute),
    Number(parts.second)
  );

  return zonedUtc - Math.floor(date.getTime() / 1000) * 1000;
}

function getTimezoneOffsetMinutes(value, timezone) {
  return getTimezoneOffsetMs(value, timezone) / 60000;
}

export function formatTimezoneOffsetLabel(value, timezone) {
  const date = toDate(value);

  if (!isValidDate(date)) {
    return '';
  }

  const timezoneOffsetMinutes = getTimezoneOffsetMinutes(date, timezone);

  return formatOffsetMinutes(timezoneOffsetMinutes);
}

export function getTimezoneOffsetDifferenceHours(value, timezone, comparisonTimezone) {
  return (
    (getTimezoneOffsetMinutes(value, timezone) -
      getTimezoneOffsetMinutes(value, comparisonTimezone)) /
    60
  );
}

export function getDateInTimezone(value, timezone) {
  const parts = getZonedParts(value, timezone);

  return new Date(
    Number(parts.year),
    Number(parts.month) - 1,
    Number(parts.day),
    Number(parts.hour),
    Number(parts.minute),
    Number(parts.second)
  );
}

export function getTimezoneLabel(timezone) {
  const normalized = normalizeTimezone(timezone);

  return getTimezoneDisplayName(normalized);
}

export function isDefaultTimezone(timezone, defaultTimezone = DEFAULT_TIMEZONE) {
  return normalizeTimezone(timezone) === normalizeTimezone(defaultTimezone);
}

export function shouldShowTimezoneLabel(timezone, defaultTimezone = DEFAULT_TIMEZONE) {
  return !isDefaultTimezone(timezone, defaultTimezone);
}

export function formatTimezoneLabel(value, timezone) {
  const date = toDate(value);
  const normalizedTimezone = normalizeTimezone(timezone);

  if (!isValidDate(date)) {
    return '';
  }

  for (const style of ['short', 'shortGeneric']) {
    for (const locale of TIMEZONE_LABEL_LOCALES) {
      const label = timezoneNamePart(date, normalizedTimezone, locale, style);

      if (label && !GMT_OFFSET_PATTERN.test(label)) {
        return label;
      }
    }
  }

  return formatTimezoneOffsetLabel(date, normalizedTimezone);
}
